import { useEffect } from 'react';
import { OG_IMAGE_URL, OG_IMAGE_WIDTH, OG_IMAGE_HEIGHT, OG_IMAGE_ALT } from '../pages/home-redesign/cityLinks';
import { SITE_NAME } from '../seo/constants';

export interface SeoMetaProps {
  title: string;
  description: string;
  url: string;
  image?: string;
  locale?: 'es' | 'en';
  noindex?: boolean;
}

/** Trim and cap meta descriptions for search snippets (~160 chars). */
export function truncateDescription(text: string, max = 160): string {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trim()}…`;
}

function upsertLink(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }
  element.href = href;
}

export const useSeoMeta = ({
  title,
  description,
  url,
  image,
  locale = 'es',
  noindex = false,
}: SeoMetaProps) => {
  useEffect(() => {
    document.title = title;
    document.documentElement.lang = locale;

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const ogLocale = locale === 'es' ? 'es_ES' : 'en_US';

    updateMetaTag('description', description);
    updateMetaTag('title', title);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:site_name', SITE_NAME, true);
    updateMetaTag('og:locale', ogLocale, true);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);

    if (image) {
      updateMetaTag('og:image', image, true);
      updateMetaTag('twitter:image', image, true);
      if (image === OG_IMAGE_URL) {
        updateMetaTag('og:image:width', String(OG_IMAGE_WIDTH), true);
        updateMetaTag('og:image:height', String(OG_IMAGE_HEIGHT), true);
        updateMetaTag('og:image:alt', OG_IMAGE_ALT, true);
      } else {
        document.querySelector('meta[property="og:image:width"]')?.remove();
        document.querySelector('meta[property="og:image:height"]')?.remove();
        document.querySelector('meta[property="og:image:alt"]')?.remove();
      }
    }

    upsertLink('canonical', url);

    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      document.querySelector('meta[name="robots"]')?.remove();
    }
  }, [title, description, url, image, locale, noindex]);
};
